import { useMemo } from 'react'

import { getDiffBetweenDates } from 'commons/utils/date'
import { Alert, DatePicker, Icon, type OptionProps, SlimButton } from 'components'
import dayjs from 'dayjs'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { Controller, useForm, useFormContext } from 'react-hook-form'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

type CustomPickerModalProps = {
  fieldName: string
  open: boolean
  previousValue: string
  setCustomInterval: (option: OptionProps) => void
}

// TODO: replace direct dayjs() usage with utils

const CustomPickerModal = ({
  fieldName,
  open,
  previousValue,
  setCustomInterval,
}: CustomPickerModalProps) => {
  const { setValue } = useFormContext()

  const handleClose = () => {
    setValue(fieldName, previousValue)
    reset(defaultValues)
  }

  const defaultValues = {
    beginDate: dayjs().subtract(6, 'months').format('YYYY-MM'),
    endDate: dayjs().format('YYYY-MM'),
  }

  const { control, trigger, handleSubmit, getValues, reset, formState } = useForm({
    defaultValues,
    mode: 'onChange',
  })

  const submitForm = handleSubmit((data) => {
    const startDate = dayjs(data.beginDate, 'YYYY-MM').startOf('month')
    const endDate = dayjs(data.endDate, 'YYYY-MM').endOf('day')

    if (startDate.format('MM/YYYY') === endDate.format('MM/YYYY')) {
      setCustomInterval({
        title: `${startDate.format('MM/YY')}`,
        value: `${startDate.format('L')} - ${endDate.format('L')}`,
      })
    } else {
      setCustomInterval({
        title: `${startDate.format('MM/YY')} a ${endDate.format('MM/YY')}`,
        value: `${startDate.format('L')} - ${endDate.format('L')}`,
      })
    }

    setValue(fieldName, `${startDate.format('L')} - ${endDate.format('L')}`)
  })

  const isValidInterval = useMemo(() => {
    const { beginDate, endDate } = getValues()
    if (formState.errors.beginDate || formState.errors.endDate) return true
    return getDiffBetweenDates(beginDate, endDate, 'month') <= 12
  }, [formState])

  return (
    <>
      {open && (
        <ModalContentLayout closeAction={handleClose} title="Período personalizado" titleSize={22}>
          <S.Content>
            <span>Indique um período de até 1 ano nos campos abaixo.</span>

            <S.FieldsWrapper>
              <Controller
                control={control}
                name="beginDate"
                render={({ field, fieldState }) => (
                  <DatePicker
                    {...field}
                    disableMaskedInput={false}
                    error={fieldState.error?.message}
                    errorState={!isValidInterval}
                    inputFormat="MM/YYYY"
                    label="Mês cota inicial"
                    maxDate={dayjs().add(2, 'year') as unknown as string}
                    minDate={dayjs().subtract(5, 'year') as unknown as string}
                    onChange={(date) => {
                      field.onChange(dayjs(date).format('YYYY-MM'))
                      trigger()
                    }}
                    views={['year', 'month']}
                  />
                )}
                rules={{
                  validate: {
                    min: (date) =>
                      dayjs(date)
                        .endOf('month')
                        .isAfter(dayjs().subtract(5, 'year').startOf('month')) || 'Data inválida.',
                  },
                }}
              />
              <Controller
                control={control}
                name="endDate"
                render={({ field, fieldState }) => (
                  <DatePicker
                    {...field}
                    disableMaskedInput={false}
                    error={fieldState.error?.message}
                    errorState={!isValidInterval}
                    inputFormat="MM/YYYY"
                    label="Mês cota final"
                    maxDate={dayjs().add(2, 'year') as unknown as string}
                    minDate={dayjs().subtract(5, 'year') as unknown as string}
                    onChange={(date) => {
                      field.onChange(dayjs(date).format('YYYY-MM'))
                      trigger()
                    }}
                    views={['year', 'month']}
                  />
                )}
                rules={{
                  validate: {
                    afterBegin: (date) =>
                      dayjs(date)
                        .endOf('month')
                        .isAfter(dayjs(getValues('beginDate')).startOf('month')) ||
                      'Período inválido.',
                    min: (date) =>
                      dayjs(date)
                        .endOf('month')
                        .isAfter(dayjs().subtract(5, 'year').startOf('month')) || 'Data inválida.',
                  },
                }}
              />
            </S.FieldsWrapper>

            {!isValidInterval && (
              <Alert fullWidth kind="error" title="O período deve ser inferior a 1 ano" />
            )}
          </S.Content>

          <S.Footer>
            <S.ButtonWrapper>
              <SlimButton
                content="Voltar"
                icon={<Icon icon={'chevron-left'} />}
                iconColor={theme.colors.neutral.high}
                onClick={handleClose}
                variant="secondary"
              />
            </S.ButtonWrapper>
            <S.ButtonWrapper>
              <SlimButton
                content="Confirmar"
                disabled={!isValidInterval || !formState.isValid}
                onClick={submitForm}
                variant="primary"
              />
            </S.ButtonWrapper>
          </S.Footer>
        </ModalContentLayout>
      )}
    </>
  )
}

export default CustomPickerModal
